import React from 'react';
import { FoundationData } from '../../types/Foundation';
import { Bot } from 'lucide-react';

interface FoundationFormProps {
  step: number;
  totalSteps: number;
  data: FoundationData;
  onUpdate: (section: keyof FoundationData, data: any) => void;
  onAskAI: (question: string) => void;
  onImproveText: (text: string, field: string) => void;
}

interface FieldHelpButtonProps {
  topic: string;
  onClick: () => void;
}

function FieldHelpButton({ topic, onClick }: FieldHelpButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="ml-2 inline-flex items-center justify-center w-5 h-5 rounded-full 
               bg-[#A3C1DA]/10 text-[#A3C1DA] hover:bg-[#A3C1DA]/20 
               transition-colors focus:outline-none focus:ring-2 
               focus:ring-offset-2 focus:ring-[#A3C1DA]"
      title={`Få hjälp med ${topic}`}
    >
      ?
    </button>
  );
}

interface TextImprovementButtonProps {
  text: string;
  onImprove: (text: string) => void;
}

function TextImprovementButton({ text, onImprove }: TextImprovementButtonProps) {
  if (!text.trim()) return null;

  return (
    <button
      type="button"
      onClick={() => onImprove(text)}
      className="mt-2 text-sm text-[#A3C1DA] hover:text-[#F2C1D1] 
               transition-colors flex items-center gap-1"
    >
      <Bot className="w-4 h-4" />
      <span>Förbättra text</span>
    </button>
  );
}

export function FoundationForm({ 
  step, 
  totalSteps, 
  data, 
  onUpdate,
  onAskAI,
  onImproveText 
}: FoundationFormProps) {
  const renderField = (
    label: string,
    helpTopic: string,
    children: React.ReactNode
  ) => (
    <div className="mb-6">
      <div className="flex items-center mb-2">
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
        <FieldHelpButton
          topic={helpTopic}
          onClick={() => onAskAI(`Kan du förklara mer om ${helpTopic.toLowerCase()}?`)}
        />
      </div>
      {children}
    </div>
  );

  const renderBasicInfo = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-[#A3C1DA] mb-6">
        Grundläggande Information
      </h3>
      {renderField("Stiftelsens namn", "val av stiftelsens namn", (
        <>
          <input
            type="text"
            value={data.basicInfo.name}
            onChange={(e) => onUpdate('basicInfo', { ...data.basicInfo, name: e.target.value })}
            className="w-full rounded-lg border-gray-300 shadow-sm 
                     focus:border-[#A3C1DA] focus:ring-[#A3C1DA]"
            placeholder="t.ex. Stiftelsen för..."
          />
          <TextImprovementButton
            text={data.basicInfo.name}
            onImprove={(text) => onImproveText(text, 'name')}
          />
        </>
      ))}
      
      {renderField("Stiftelsens ändamål", "formulering av stiftelsens ändamål", (
        <>
          <textarea
            value={data.basicInfo.purpose}
            onChange={(e) => onUpdate('basicInfo', { ...data.basicInfo, purpose: e.target.value })}
            rows={4}
            className="w-full rounded-lg border-gray-300 shadow-sm 
                     focus:border-[#A3C1DA] focus:ring-[#A3C1DA]"
            placeholder="Beskriv stiftelsens syfte och målsättning..."
          />
          <TextImprovementButton
            text={data.basicInfo.purpose}
            onImprove={(text) => onImproveText(text, 'purpose')}
          />
        </>
      ))}
      
      {renderField("Typ av stiftelse", "olika typer av stiftelser", (
        <select
          value={data.basicInfo.type}
          onChange={(e) => onUpdate('basicInfo', { ...data.basicInfo, type: e.target.value })}
          className="w-full rounded-lg border-gray-300 shadow-sm 
                   focus:border-[#A3C1DA] focus:ring-[#A3C1DA]"
        >
          <option value="">Välj typ...</option>
          <option value="allmännyttig">Allmännyttig stiftelse</option>
          <option value="familjestiftelse">Familjestiftelse</option>
          <option value="näringsdrivande">Näringsdrivande stiftelse</option>
          <option value="insamling">Insamlingsstiftelse</option>
        </select>
      ))}
    </div>
  );

  const renderGovernance = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-[#A3C1DA] mb-6">
        Styrelse och Förvaltning
      </h3>
      {renderField("Beslutsprocess", "stiftelsens beslutsprocess", (
        <>
          <textarea
            value={data.governance.decisionMaking}
            onChange={(e) => onUpdate('governance', { ...data.governance, decisionMaking: e.target.value })}
            rows={4}
            className="w-full rounded-lg border-gray-300 shadow-sm 
                     focus:border-[#A3C1DA] focus:ring-[#A3C1DA]"
            placeholder="Beskriv hur beslut fattas i stiftelsen..."
          />
          <TextImprovementButton
            text={data.governance.decisionMaking}
            onImprove={(text) => onImproveText(text, 'decisionMaking')}
          />
        </>
      ))}
      
      {renderField("Stadgar", "utformning av stadgar", (
        <>
          <textarea
            value={data.governance.statutes}
            onChange={(e) => onUpdate('governance', { ...data.governance, statutes: e.target.value })}
            rows={6}
            className="w-full rounded-lg border-gray-300 shadow-sm 
                     focus:border-[#A3C1DA] focus:ring-[#A3C1DA]"
            placeholder="Ange stiftelsens stadgar..."
          />
          <TextImprovementButton
            text={data.governance.statutes}
            onImprove={(text) => onImproveText(text, 'statutes')}
          />
        </>
      ))}
    </div>
  );

  const renderFinancial = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-[#A3C1DA] mb-6">
        Ekonomi och Revision
      </h3>
      {renderField("Räkenskapsperiod", "val av räkenskapsperiod", (
        <input
          type="text"
          value={data.financial.accountingPeriod}
          onChange={(e) => onUpdate('financial', { ...data.financial, accountingPeriod: e.target.value })}
          className="w-full rounded-lg border-gray-300 shadow-sm 
                   focus:border-[#A3C1DA] focus:ring-[#A3C1DA]"
          placeholder="t.ex. 1 januari - 31 december"
        />
      ))}
      
      {renderField("Revisionskrav", "revisionskrav för stiftelsen", (
        <>
          <textarea
            value={data.financial.auditRequirements}
            onChange={(e) => onUpdate('financial', { ...data.financial, auditRequirements: e.target.value })}
            rows={4}
            className="w-full rounded-lg border-gray-300 shadow-sm 
                     focus:border-[#A3C1DA] focus:ring-[#A3C1DA]"
            placeholder="Beskriv stiftelsens revisionskrav..."
          />
          <TextImprovementButton
            text={data.financial.auditRequirements}
            onImprove={(text) => onImproveText(text, 'auditRequirements')}
          />
        </>
      ))}
    </div>
  );

  const renderLegal = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-[#A3C1DA] mb-6">
        Juridiska Aspekter
      </h3>
      {renderField("Registreringsdetaljer", "registrering av stiftelsen", (
        <>
          <textarea
            value={data.legal.registrationDetails}
            onChange={(e) => onUpdate('legal', { ...data.legal, registrationDetails: e.target.value })}
            rows={4}
            className="w-full rounded-lg border-gray-300 shadow-sm 
                     focus:border-[#A3C1DA] focus:ring-[#A3C1DA]"
            placeholder="Ange information om registrering..."
          />
          <TextImprovementButton
            text={data.legal.registrationDetails}
            onImprove={(text) => onImproveText(text, 'registrationDetails')}
          />
        </>
      ))}
      
      {renderField("Skattestatus", "stiftelsens skattestatus", (
        <>
          <textarea
            value={data.legal.taxStatus}
            onChange={(e) => onUpdate('legal', { ...data.legal, taxStatus: e.target.value })}
            rows={4}
            className="w-full rounded-lg border-gray-300 shadow-sm 
                     focus:border-[#A3C1DA] focus:ring-[#A3C1DA]"
            placeholder="Beskriv stiftelsens skattestatus..."
          />
          <TextImprovementButton
            text={data.legal.taxStatus}
            onImprove={(text) => onImproveText(text, 'taxStatus')}
          />
        </>
      ))}
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 1:
        return renderBasicInfo();
      case 2:
        return renderGovernance();
      case 3:
        return renderFinancial();
      case 4:
        return renderLegal();
      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-lg">
      {renderStep()}
    </div>
  );
}